<template lang="pug">
v-container.pa-8(fluid)
  .d-flex.align-center.mb-4
    v-btn.mr-4(icon small)
      v-icon(color="primary" @click="onClickBack") mdi-arrow-left
    .text-2xl.font-semibold {{ $t('printers') }}
  .text-lg.font-medium {{ $t('thermal_printers') }}
  .text-sm {{ $t('thermal_printers_description') }}
  v-row.pb-2.pb-md-0.mt-4(no-gutters)
    v-col(cols="12" md="8")
      v-tabs(v-model="printerTab" color="basic700" :height="44" :grow="isMobileScreen")
        v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="openAddPrinterDialog = true") {{ $t('add_item', { item: $t('new').toLowerCase() }) }}
  v-divider
  filter-row.my-2(:filters.sync="filters" ref="filter")
  .data-table
    v-data-table.mt-sm-4.pb-6(:headers="headers" :items="printers" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" disable-sort @click:row="" loader-height="2")
      //- Paper size
      template(v-slot:item.paper_size="{ item }")
        .text-sm {{ getPaperSize(item) }}
      //- Host
      template(v-slot:item.host="{ item }")
        .text-sm {{ getHostName(item) }}
      //- Enabled
      template(v-slot:item.enabled="{ item }")
        y-switch.mb-n3.mx-0.pa-0(v-model="item.enabled" @change="updateEnabled(item, item.enabled)")
      //- Default
      template(v-slot:item.is_default="{ item }")
        y-switch.mb-n3.mx-0.pa-0(v-model="item.is_default" :disabled="!printerGroup"  @change="updateDefault(item, item.is_default)")
      //- Action
      template(v-slot:item.action="{ item }")
        div.py-3
          v-menu(offset-y left rounded="lg")
            template(#activator="{ on }")
              v-btn.ml-4(icon small v-on="on")
                v-icon mdi-dots-horizontal
            v-list.py-0
              template(v-for="(action, actionIdx) in actions")
                v-divider(v-if="actionIdx !== 0")
                v-list-item(:key="'action-' + actionIdx" @click="onClickAction(item, action)")
                  v-list-item-title.text-sm.basic800--text(:class="[action.textColor]") {{ action.text }}
  div.d-flex.align-center(v-if="totalCount > pageSize")
    v-spacer
    div
      yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" attach menu-top @input="pageChanged")
  .dialogs
    add-printer-dialog(v-if="openAddPrinterDialog" :open.sync="openAddPrinterDialog" :addedPrinters="printers" :parentPrinter="selectedPrinter" @updated="fetchPrinters" @closed="selectedPrinter = null")
    delete-printer-dialog(v-if="openConfirmDeleteTemplateDialog" :open.sync="openConfirmDeleteTemplateDialog" :selectedPrinter="selectedPrinter" :loading="isDeleting" @deleted="fetchPrinters" @closed="selectedPrinter = null")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import FilterRow from '@/components/clinic/manage/printer/FilterRow.vue'
import AddPrinterDialog from '@/components/clinic/dialogs/AddPrinterDialog.vue'
import DeletePrinterDialog from '@/components/clinic/dialogs/DeletePrinterDialog.vue'

export default {
  components: {
    FilterRow, AddPrinterDialog, DeletePrinterDialog
  },
  data() {
    return {
      printerTab: 0,
      searchText: null,
      showSearchField: false,
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      isFetching: false,
      openAddPrinterDialog: false,
      openConfirmDeleteTemplateDialog: false,
      filters: [],
      printers: [],
      selectedPrinter: null,
      isDeleting: false,
      headers: [
        {
          id: 'PRINTER',
          text: 'PRINTER',
          value: 'name',
          width: '35%'
        },
        // {
        //   id: 'PAPER SIZE',
        //   text: 'PAPER SIZE',
        //   value: 'paper_size',
        //   width: '20%'
        // },
        {
          id: 'HOST',
          text: 'HOST',
          value: 'host',
          width: '20%'
        },
        {
          id: 'DEFAULT',
          text: 'DEFAULT',
          value: 'is_default',
        },
        {
          id: 'ENABLE PRINTER',
          text: 'ENABLE PRINTER',
          value: 'enabled'
        },
        {
          id: 'ACTION',
          text: 'ACTION',
          value: 'action',
          align: 'end'
        }
      ],
      actions: [
        {
          text: this.$t('edit_printer'),
          textColor: 'neutral700--text'
        },
        {
          text: this.$t('delete_printer'),
          textColor: 'danger500--text'
        }
      ],
      printerGroup: null
    }
  },
  async mounted () {
    try {
      this.isFetching = true
      const allFetching = [this.fetchPrinters(), this.fetchPrinterGroups()]
      await Promise.all([...allFetching])
      this.isFetching = false
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
  },
  watch: {
    filters () {
      this.currentPage = 1
      this.fetchPrinters()
    },
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    params () {
      let params = {
        search: this.searchText || null,
        page_size: this.pageSize,
        page: this.currentPage
      }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      return params
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged : debounce(function () {
      this.fetchPrinters()
    }, 750),
    onClickBack () {
      this.$router.push('/admin/clinic/settings')
    },
    pageChanged(num) {
      this.page = num
      this.fetchPrinters()
    },
    getHostName (item) {
      return item?.host?.hostname || null
    },
    getPaperSize (item) {
      if (item.paper_size) {
        return `${item.paper_width}${item.paper_size_unit} x ${item.paper_height}${item.paper_size_unit}`
      }
      return null
    },
    onClickAction (item, action) {
      switch (action.text) {
        case this.$t('edit_printer'):
          this.selectedPrinter = item
          this.openAddPrinterDialog = true
          return

        case this.$t('delete_printer'):
          this.selectedPrinter = item
          this.openConfirmDeleteTemplateDialog = true
          return
      
        default:
          return
      }
    },
    async fetchPrinters () {
      return this.$axios.get(`/iot/${this.company.slug}/printers`, { params: this.params })
        .then(({ data }) => {
          this.printers = data.results
          this.totalCount = data.count
        })
    },
    async updateEnabled (item, enabled) {
      this.isFetching = true
      try {
        await this.$axios.patch(`/iot/${this.company.slug}/printers/${item.id}`, { enabled })
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    fetchPrinterGroups () {
      const printerGroupName = 'Drug Label Printers'
      const params = {
        search: printerGroupName
      }
      return this.$axios.get(`/iot/${this.company.slug}/printer_groups`, { params })
        .then(({ data }) => {
          if (data.count > 0) {
            this.printerGroup = data.results[0]
            return
          }
          this.createNewPrinterGroup(printerGroupName)
        })
    },
    async createNewPrinterGroup (groupName) {
      try {
        const { data } = await this.$axios.post(`/iot/${this.company.slug}/printer_groups`, { name: groupName })
        this.printerGroup = data
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    async updateDefault (item, enabled) {
      this.isFetching = true
      try {
        if (item.group?.id !== this.printerGroup.id) {
          await this.$axios.patch(`/iot/${this.company.slug}/printers/${item.id}`, { group: this.printerGroup.id })
        }
        if (enabled) {
          await this.$axios.patch(`/iot/${this.company.slug}/printer_groups/${this.printerGroup.id}`, { default_printer: item.id })
          this.fetchPrinters()
        } else {
          await this.$axios.patch(`/iot/${this.company.slug}/printer_groups/${this.printerGroup.id}`, { default_printer: null })
        }
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch (error) {
        item.is_default = !item.is_default
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
</style>